<template>
  <div id="page-add-inventory">
    <vs-card>
      <div class="vx-row px-5 pt-5">
        <div class="vx-col w-full md:w-1/2 px-8">
          <div class="vx-row mb-2">
            <label for="">Product Name <span class="text-danger">*</span></label>
            <vs-input icon="icon icon-list" v-model="form.inventory_name" icon-pack="feather" class="w-full" name="Product Name" v-validate="'required'" />
            <span class="text-danger text-sm" v-show="errors.has('Product Name')">{{ errors.first('Product Name') }}</span>
          </div>

          <div class="vx-row mb-2">
            <label for="">Category <span class="text-danger">*</span></label>
            <select-2
              autocomplete
              class="w-full"
              :value="form.categoryId"
              @input="(item) => (form.categoryId = item && item.value)"
              name="category"
              v-validate="'required'"
              placeholder="Select Category"
              action="inventory/getCategoryOption"
            />
            <span class="text-danger text-sm" v-show="errors.has('category')">{{ errors.first('category') }}</span>
          </div>

          <div class="vx-row mb-2">
            <label for="">Weight Type <span class="text-danger">*</span></label>
            <select-2
              autocomplete
              class="w-full"
              v-validate="'required'"
              :value="form.weight_type"
              @input="(item) => (form.weight_type = item && item.value)"
              name="Weight Type"
              placeholder="Select Weight Type"
              :options="weightTypeOptions"
            />
            <span class="text-danger text-sm" v-show="errors.has('Weight Type')">{{ errors.first('Weight Type') }}</span>
          </div>

          <div class="vx-row mb-2">
            <vs-input
              step="0.001"
              type="number"
              @change="
                (event) => {
                  form.weight_per_package = mathRound(event.target.value ? parseFloat(event.target.value) : 0, 3)
                }
              "
              icon="icon icon-file-text"
              v-model="form.weight_per_package"
              icon-pack="feather"
              class="w-full"
              label="Weight Per Package"
              name="Weight Per Package"
            />
          </div>

          <div class="vx-row mb-2">
            <vs-input
              step="0.001"
              type="number"
              @change="(event) => (form.quantity = mathRound(event.target.value ? parseFloat(event.target.value) : 0, 3))"
              icon="icon icon-file-text"
              v-model="form.quantity"
              icon-pack="feather"
              class="w-full"
              label="Quantity"
              name="Quantity"
            />
          </div>
        </div>
        <div class="vx-col w-full md:w-1/2 px-8">
          <div class="vx-row mb-2">
            <vs-input icon="icon icon-edit" v-model="form.description" icon-pack="feather" class="w-full" label="Description" name="Description" />
          </div>

          <div class="vx-row mb-2">
            <div class="block">
              <vs-input icon="icon icon-edit" v-model="form.batchNumber" icon-pack="feather" class="w-full" label="Batch" name="Batch" />
            </div>
            <div class="mt-4 ml-6 flex flex-wrap items-center justify-start">
              <vs-button color="success" type="relief" @click="generateBatchNumber">Generate</vs-button>
            </div>
          </div>

          <div class="vx-row mb-2">
            <vx-tooltip class="w-full" position="left" text="This is the price per unit that you paid. Example - If you purchased 500 grams for $500, then the amount you type here is 1.00">
              <vs-input
                icon="icon icon-dollar-sign"
                v-model="form.price"
                type="number"
                step="0.01"
                @change="(event) => (form.price = mathRound(event.target.value ? parseFloat(event.target.value) : 0, 2))"
                icon-pack="feather"
                class="w-full"
                label="Price"
                name="Price"
              />
            </vx-tooltip>
          </div>

          <div class="vx-row mb-2">
            <vs-input
              icon="icon icon-edit"
              type="number"
              step="0.001"
              @change="(event) => (form.waste_in_pounds = mathRound(event.target.value ? parseFloat(event.target.value) : 0, 3))"
              v-model="form.waste_in_pounds"
              icon-pack="feather"
              class="w-full"
              label="Waste in Pounds"
              name="Waste in Pounds"
            />
          </div>

          <div class="vx-row mb-2">
            <label for="">Manufacturer/Supplier <span class="text-danger">*</span></label>
            <select-2
              class="w-full"
              :value="form.supplierId"
              @input="
                (item) => {
                  this.form.supplierId = item && item.value
                  this.form.supplier_type = item && (item.type === 'user' ? 'user' : 'supplier')
                }
              "
              name="Manufacturer/Supplier"
              v-validate="'required'"
              placeholder="Select Manufacturer/Supplier"
              autocomplete
              :extra="[{ label: 'My Business', value: user.id, type: 'user' }]"
              action="supplier/getSupplierOption"
            />
            <span class="text-danger text-sm" v-show="errors.has('Manufacturer/Supplier')">{{ errors.first('Manufacturer/Supplier') }}</span>
          </div>
        </div>

        <div class="vx-col w-full pt-5">
          <div slot="no-body" class="tabs-container flex flex-wrap">
            <vs-tabs>
              <vs-tab label="Add Ingredient" icon-pack="feather" icon="icon-user">
                <div class="tab-text">
                  <div class="grid grid-cols-1 md:grid-cols-4 gap-6 ml-auto">
                    <ingredient
                      ref="ingredient"
                      @add-ingredient="addIngredient(i)"
                      @remove-ingredient="removeIngredient(i)"
                      @inventory-changed="inventoryChanged(i)"
                      :title="`Ingredient-${i + 1}`"
                      :key="i"
                      v-for="(ingredient, i) in form.ingredients"
                      :ingredient.sync="form.ingredients[i]"
                      class="mt-4"
                    />
                  </div>
                </div>
              </vs-tab>
            </vs-tabs>
          </div>
        </div>
      </div>

      <!-- Save & Reset Button -->
      <div class="vx-row">
        <div class="vx-col w-full">
          <div class="mt-8" vs-align="center" vs-type="flex" vs-justify="center">
            <vs-button class="mr-2 vs-con-loading__container" id="add-inventory-button" type="relief" @click="save_changes" :disabled="!validateForm">Add Inventory</vs-button>
            <vs-button to="/inventory/list" color="danger" type="relief" class="text-left">Cancel</vs-button>
          </div>
        </div>
      </div>
    </vs-card>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import DynamicSelect from '@/components/custom/form-elements/DynamicSelect.vue'
import Select2 from '@/components/custom/form-elements/Select2.vue'
import { weightTypes, weightMixins } from '@/utils/weightType'
import Ingredient from '@/views/inventories/components/Ingredient.vue'

export default {
  name: 'CreateInventory',
  components: {
    DynamicSelect,
    Ingredient,
    Select2
  },
  data() {
    const emptyIngredient = {
      id: null,
      ingredientId: null,
      weightType: null,
      ingredient_quantity: 0.0,
      ingredient_avail_quantity: 0.0
    }
    return {
      emptyIngredient,
      form: {
        inventory_name: '',
        weight_per_package: 0,
        quantity: 0,
        weightType: null,
        categoryId: null,
        description: '',
        supplierId: null,
        supplier_type: null,
        batchNumber: null,
        price: 0,
        waste_in_pounds: 0,
        ingredients: [emptyIngredient]
      },
      weightTypeOptions: weightTypes
    }
  },
  mixins: [weightMixins],
  async mounted() {
    this.generateBatchNumber()
    await this.getIngredientOption()
    // this.form.supplierId = this.user.id
    // this.form.supplier_type = 'user'
  },
  computed: {
    ...mapState('inventory', ['loading']),
    ...mapState('auth', ['user']),
    ...mapState('product', ['loading1', 'inventoryCategories']),

    validateForm() {
      //check all the fields and ingedients details to
      return !this.errors.any() && !(this.$refs['ingredient'] && this.$refs['ingredient'].map((child) => child.errors.any()).find((bool) => bool))
    }
  },
  methods: {
    ...mapActions('inventory', { insertInventory: 'insert', getIngredientOption: 'getIngredientOption' }),
    addIngredient(i) {
      this.form.ingredients.splice(i + 1, 0, this.emptyIngredient)
    },
    removeIngredient(i) {
      if (this.form.ingredients.length !== 1) {
        this.form.ingredients.splice(i, 1)
      }
    },
    inventoryChanged(i) {
      if (this.$refs['ingredient']) {
        this.$refs['ingredient'].reduce((coll, child) => {
          if (child.ingredient.ingredientId && coll.includes(child.ingredient.ingredientId)) {
            this.$vs.notify({
              title: 'Error',
              text: 'Ingredient cannot be selected twice!.',
              iconPack: 'feather',
              icon: 'icon-alert-circle',
              color: 'danger'
            })
            this.form.ingredients[i].ingredientId = null
            this.form.ingredients[i].weightType = null
            this.form.ingredients[i].ingredient_quantity = 0
          } else {
            coll.push(child.ingredient.ingredientId)
          }
          return coll
        }, [])
      }
    },
    generateBatchNumber() {
      this.form.batchNumber = `BN${new Date().getTime()}`
    },
    async save_changes() {
      //validate inventory details
      const check1 = await this.$validator.validate()
      //validate all ingredient details to
      const check2 = (await Promise.all(this.$refs['ingredient'].map((child) => child.$validator.validate()))).filter((bool) => !bool).length === 0
      if (!check1 || !check2) {
        this.$vs.notify({
          title: 'Error',
          text: 'please fill the form first',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger'
        })
        return false
      }
      try {
        const { message } = await this.insertInventory({
          ...this.form,
          price: parseFloat(this.form.price),
          quantity: parseFloat(this.form.quantity),
          ingredients: this.form.ingredients.filter((item) => !!item.ingredientId)
        })
        this.$router.push({ name: 'inventory-list' })
        this.$vs.notify({
          title: 'Success',
          text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success'
        })
      } catch ({ message }) {
        this.$vs.notify({
          title: 'Error',
          text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger'
        })
      }
    }
  },
  watch: {
    loading() {
      if (this.loading) {
        this.$vs.loading({
          container: '#page-add-inventory',
          scale: 0.45
        })
      } else {
        this.$vs.loading.close('#page-add-inventory > .con-vs-loading')
      }
    },

    loading1() {
      if (this.loading1) {
        this.$vs.loading({
          container: '#page-add-inventory',
          scale: 0.45
        })
      } else {
        this.$vs.loading.close('#page-add-inventory > .con-vs-loading')
      }
    },

    // remove unit weight type based on category
    'form.categoryId'() {
      // remove unit weight type
      this.weightTypeOptions = weightTypes.reduce((acc, item) => {
        if (
          !(
            item.value === 'unit' &&
            ![
              this.inventoryCategories.ANCILLARY,
              this.inventoryCategories.OTHER,
              this.inventoryCategories.EDIBLE,
              this.inventoryCategories.TINCTURE,
              this.inventoryCategories.TOPICAL,
              this.inventoryCategories.TRANSDERMALPATCH,
              this.inventoryCategories.CAPSULE,
              this.inventoryCategories.PREROLLS,
              this.inventoryCategories.COMBINEDCATEGORY,
              this.inventoryCategories.SUPPOSITORY,
              this.inventoryCategories.MATURE_PLANT,
              this.inventoryCategories.IMMATURE_PLANT,
              this.inventoryCategories.EXTRACT,
              this.inventoryCategories.CONCENTRATE
            ].includes(this.form.categoryId)
          )
        ) {
          this.form.weight_type = null
          acc.push(item)
        }

        if (item.value === 'unit' && [this.inventoryCategories.MATURE_PLANT, this.inventoryCategories.IMMATURE_PLANT].includes(this.form.categoryId)) {
          this.form.weight_type = item.value
          acc = []
          acc.push(item)
        }

        return acc
      }, [])
    }
  }
}
</script>
